import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import { NOT_CHAIN_APPROVER_ROLES, SUPERADMIN_ONLY_LABEL, roleKey } from "../../constants/roles";
import { isSuperadminUser } from "../../utils/leadershipRoles";
import { superadminErrorMessage, useCurrentUser } from "../../utilities/currentUser";

export default function RoleApprovalSettings() {
  const { user } = useCurrentUser();
  // Admin may view the approval chain; only a Superadmin may change and save it.
  const canEdit = isSuperadminUser(user);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMotherCompany, setIsMotherCompany] = useState(false);
  const [chainPreview, setChainPreview] = useState([]);
  const [previewCompanyId, setPreviewCompanyId] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);

  // ===============================
  // 1️⃣ Fetch companies
  // ===============================
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await api.get("/api/companies");
        setCompanies(res.data);
        if (res.data.length > 0) setSelectedCompany(res.data[0].id);
      } catch (err) {
        console.error(err);
      }
    };
    fetchCompanies();
  }, []);

  // ===============================
  // 2️⃣ Fetch roles for selected company
  // ===============================
  useEffect(() => {
    if (!selectedCompany) return;

    const fetchRoles = async () => {
      try {
        const res = await api.get(
          `/api/company/${selectedCompany}/role-approval-settings`,
        );
        // Superadmin and the retired Admin are not approval-chain roles (the API drops them too).
        const nonApproverKeys = NOT_CHAIN_APPROVER_ROLES.map(roleKey);
        setRoles((res.data.roles || res.data).filter((r) => !nonApproverKeys.includes(roleKey(r.role_name))));
        setIsMotherCompany(res.data.is_mother_company || false);
      } catch (err) {
        console.error(err);
      }
    };

    fetchRoles();
  }, [selectedCompany]);

  // ===============================
  // 3️⃣ Toggle handler
  // ===============================
  const handleToggle = (roleId) => {
    if (!canEdit) return;
    const updated = roles.map((r) =>
      r.role_id === roleId ? { ...r, is_active: !r.is_active } : r,
    );
    setRoles(updated);
  };

  // ===============================
  // 4️⃣ Save handler
  // ===============================
  const handleSave = async () => {
    if (!selectedCompany || !canEdit) return;

    setLoading(true);
    setSaveMessage(null);
    try {
      const res = await api.post(`/api/company/${selectedCompany}/role-approval-settings`, {
        roles: roles.map((r) => ({
          role_id: r.role_id,
          is_active: r.is_active,
        })),
      });
      setSaveMessage({ type: "success", text: res.data.message || "Saved successfully ✅" });
    } catch (err) {
      console.error(err);
      setSaveMessage({ type: "error", text: superadminErrorMessage(err, "Error saving ❌") });
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // 5️⃣ Preview approval chain
  // ===============================
  const handlePreviewChain = async (companyId) => {
    try {
      const res = await api.get(`/api/approval-chain/${companyId}`);
      setChainPreview(res.data.chain || []);
      const company = companies.find(c => c.id == companyId);
      setPreviewCompanyId(company ? company.name : `Company #${companyId}`);
    } catch (err) {
      console.error(err);
      setChainPreview([]);
    }
  };

  const activeRolesCount = roles.filter((r) => r.is_active).length;

  return (
    <div className="container mt-4">
      <div className="card shadow-lg border-0 rounded-4">
        <div className="card-body">
          {/* Header */}
          <div className="card-header  text-white text-center rounded-top-4 d-flex justify-content-between align-items-center" style={{backgroundColor:"#bd06d3"}}>
            <h5 className="mb-0 fw-bold text-white"> Role Approval Settings</h5>
            <span className="badge bg-light text-dark">
              {activeRolesCount} active role(s)
            </span>
          </div>



          {!canEdit && (
            <div className="alert alert-info mt-3 mb-0">
              {SUPERADMIN_ONLY_LABEL}: you can view the approval chain, but only a Superadmin can change it.
            </div>
          )}

          {/* Company Display */}
          <div className="row mb-4 mt-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Company:</label>
              <input
                type="text"
                className="form-control bg-light shadow-sm"
                value={companies.find(c => c.id === Number(selectedCompany))?.name || "Iconic Greenland Property Ltd"}
                readOnly
              />
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <button
                className="btn btn-outline-primary"
                onClick={() => handlePreviewChain(selectedCompany)}
                disabled={!selectedCompany}
              >
                👁️ Preview Approval Chain
              </button>
            </div>
          </div>

          {/* Roles Table */}
          <div className="table-responsive">
            <table className="table table-hover align-middle text-center" style={{ marginTop: "10px" }}>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Role</th>
                  <th>Hierarchy (Sequence)</th>
                  <th>Approval Required</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role, index) => (
                  <tr key={role.role_id} className={role.is_active ? "table-success" : ""}>
                    <td>{index + 1}</td>
                    <td className="fw-semibold">{role.role_name}</td>
                    <td>
                      <span className="badge bg-secondary">{role.sequence || 0}</span>
                    </td>
                    <td>
                      <div className="form-check form-switch d-flex justify-content-center">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={role.is_active}
                          onChange={() => handleToggle(role.role_id)}
                          disabled={!canEdit}
                          title={canEdit ? undefined : SUPERADMIN_ONLY_LABEL}
                          style={{ width: "3em", height: "1.5em" }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {roles.length === 0 && (
                  <tr>
                    <td colSpan="4" style={{ textAlign: "center" }}>
                      No roles found for this company.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Save Message */}
          {saveMessage && (
            <div className={`alert ${saveMessage.type === "success" ? "alert-success" : "alert-danger"} mt-2`}>
              {saveMessage.text}
            </div>
          )}

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={loading || !canEdit}
            title={canEdit ? undefined : SUPERADMIN_ONLY_LABEL}
            className="btn btn-success px-4 py-2 shadow-sm mt-2"
          >
            {loading ? "Saving..." : "💾 Save Settings"}
          </button>

          {/* Chain Preview */}
          {chainPreview.length > 0 && (
            <div className="mt-4">
              <h6 className="fw-bold">
                📋 Approval Chain Preview for: <span className="text-primary">{previewCompanyId}</span>
              </h6>
              <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                {chainPreview.map((step, idx) => (
                  <React.Fragment key={step.step}>
                    <div className={`badge ${step.is_mother_company ? "bg-warning text-dark" : "bg-primary"} p-2`}
                      style={{ fontSize: "0.9em" }}>
                      <strong>Step {step.step}:</strong> {step.role_name}
                      <br />
                      <small>{step.company_name}</small>
                    </div>
                    {idx < chainPreview.length - 1 && (
                      <span style={{ fontSize: "1.5em" }}>→</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="mt-2">
                <small className="text-muted">
                  <span className="badge bg-primary me-1">&nbsp;</span> Sister company roles &nbsp;
                  <span className="badge bg-warning">&nbsp;</span> Mother company roles (apply to all)
                </small>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
